
import { useAuthStore } from '@/lib/stores/auth';
import { useEffect, useRef } from 'react';

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * AuthProvider - Manages authentication state ONLY
 * 
 * CRITICAL: This component does NOT handle navigation/redirects
 * Navigation is handled by React Router guards (PublicOnlyRoute, ProtectedRoute)
 * 
 * This separation prevents redirect loops caused by:
 * - Multiple navigation sources conflicting
 * - Effect re-runs before navigation completes
 * - Timing issues between auth state and router state
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const { initializeAuth, isInitialized, isAuthenticated, user } = useAuthStore();
  const hasInitializedRef = useRef(false);

  // Initialize once on mount. Store-level initializeAuth handles deduping and listener lifecycle.
  useEffect(() => {
    if (hasInitializedRef.current) {
      return;
    }

    hasInitializedRef.current = true;

    initializeAuth()
      .then(() => {
        // Clean up redirect state if present
        sessionStorage.removeItem('pendingGoogleAuth');
        // Clean up URL parameters
        const url = new URL(window.location.href);
        url.searchParams.delete('auth');
        url.searchParams.delete('message');
        window.history.replaceState({}, '', url.toString());
      })
      .catch((error) => {
        console.error('[AuthProvider] Auth initialization error:', error);
      });
  }, [initializeAuth]);

  // Debug logging (development only)
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('[AuthProvider] State:', {
        isInitialized,
        isAuthenticated,
        hasUser: !!user,
        userEmail: user?.email,
      });
    }
  }, [isInitialized, isAuthenticated, user]);

  // NOTE: NO REDIRECT LOGIC HERE
  // Navigation is handled by React Router guards:
  // - PublicOnlyRoute: Redirects authenticated users from public routes
  // - ProtectedRoute: Redirects unauthenticated users from protected routes

  return <>{children}</>;
}
